import { collectionsData, type CollectionProduct } from '~/data/collections'
import type { Artist } from '~/types/artist'
import type { SKU } from '~/types/sku'
import { ref } from 'vue'

export const createArtistSlug = (brandName: string): string => {
  return brandName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export const isLightColor = (color: string): boolean => {
  if (!color || !color.startsWith('#')) return true
  
  const hex = color.slice(1)
  const fullHex = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex
  const r = parseInt(fullHex.substring(0, 2), 16)
  const g = parseInt(fullHex.substring(2, 4), 16)
  const b = parseInt(fullHex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance >= 0.5
}

const getAllProducts = (): CollectionProduct[] => {
  return collectionsData.flatMap((collection) => collection.products ?? [])
}

export const useArtists = () => {
  const dataSource = ref<'airtable' | 'fallback' | undefined>(undefined)
  const dataReason = ref<string | undefined>(undefined)

  type ArtistsApiResponse = { data: Artist[]; meta?: { source?: 'airtable' | 'fallback'; reason?: string } }

  // Build-time only: data is fetched at prerender and embedded in payload. Client never refetches (no runtime Airtable when static).
  const { data: artistsData, pending, error, refresh } = useAsyncData<ArtistsApiResponse>(
    'artists-data',
    () => $fetch<ArtistsApiResponse>('/api/artists'),
    {
      server: true,
      lazy: false,
      default: () => ({ data: [], meta: undefined }),
      // Prefer payload so static deploy never calls /api/artists (SEO: content is in initial HTML).
      getCachedData: (key) => useNuxtApp().payload.data[key] as ArtistsApiResponse | undefined,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  )

  // Ensure dataSource/dataReason are set when hydrated via SSR body only
  watch(() => artistsData.value?.meta, (meta) => {
    if (!meta) return
    if (meta.source) dataSource.value = meta.source
    if (meta.reason) dataReason.value = meta.reason
  }, { immediate: true })

  const normalizeSlug = (slug: string) =>
    slug.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  const getArtistBySlug = (slug: string): Artist | undefined => {
    const list: Artist[] = artistsData.value?.data ?? []
    const normalizedSlug = normalizeSlug(slug)
    return (
      list.find((artist: Artist) => artist.slug === slug) ??
      list.find((artist: Artist) => normalizeSlug(artist.slug) === normalizedSlug)
    )
  }

  const getAllArtists = (): Artist[] => (artistsData.value?.data ?? [])

  /**
   * Convert SKU to CollectionProduct format for display compatibility
   * Note: The component will handle locale-specific descriptions from the SKU object
   */
  const convertSKUToCollectionProduct = (sku: SKU, artistName: string): CollectionProduct => {
    // Use discounted price if available, otherwise use regular price
    const displayPrice = sku.discountedPrice || sku.price || '0'
    const priceStr = typeof displayPrice === 'number' ? displayPrice.toString() : displayPrice
    
    // Determine status based on SKU fields
    let status = 'available'
    if (sku.active === false) {
      status = 'sold-out'
    } else if (sku.inCatalogWithInventory === false) {
      status = 'sold-out'
    } else if (sku.inventory !== undefined) {
      const inventoryNum = typeof sku.inventory === 'number' ? sku.inventory : parseFloat(String(sku.inventory))
      if (inventoryNum === 0) {
        status = 'sold-out'
      }
    }
    
    // Get image - prefer pics array first image, then image field
    const image = sku.pics?.[0] || sku.image || ''
    
    // Build name with variation if present
    let productName = sku.name || sku.sku
    if (sku.variation || sku.variationStr) {
      productName = `${productName} ${sku.variationStr || sku.variation || ''}`.trim()
    }
    
    // Default description (component will use locale-specific one from SKU object)
    const description = sku.descriptionEn || sku.descriptionDe || sku.descriptionIt || ''
    
    return {
      id: sku.id,
      brand: artistName,
      name: productName,
      description: description,
      material: sku.material || '',
      price: priceStr,
      image: image,
      status: status,
      // Store original SKU for reference so component can access locale-specific fields
      sku: sku
    } as CollectionProduct & { sku?: SKU }
  }

  /**
   * Get products for an artist - prioritizes SKUs from Airtable, falls back to collection products
   */
  const getArtistProducts = (brandName: string, artist?: Artist): CollectionProduct[] => {
    if (import.meta.client && process.env.NODE_ENV === 'development') {
      console.log('[getArtistProducts] Called for:', brandName)
      console.log('[getArtistProducts] Artist has products?', !!artist?.products)
      console.log('[getArtistProducts] Artist products count:', artist?.products?.length)
      console.log('[getArtistProducts] Artist ID:', artist?.id)
    }
    
    // If artist has SKUs from Airtable, use those
    if (artist?.products && artist.products.length > 0) {
      const converted = artist.products.map((sku) => convertSKUToCollectionProduct(sku, brandName))
      if (import.meta.client && process.env.NODE_ENV === 'development') {
        console.log('[getArtistProducts] Converted SKUs to products:', converted.length)
      }
      return converted
    }
    
    // Fallback to collection products
    const allProducts = getAllProducts()
    const filtered = allProducts.filter((product) => product.brand === brandName)
    if (import.meta.client && process.env.NODE_ENV === 'development') {
      console.log('[getArtistProducts] Using fallback collection products:', filtered.length)
    }
    return filtered
  }

  const getOtherArtists = (currentSlug: string, limit: number = 6): Artist[] => {
    return getAllArtists()
      .filter((artist) => artist.slug !== currentSlug)
      .slice(0, limit)
  }

  return {
    getArtistBySlug,
    getAllArtists,
    getArtistProducts,
    getOtherArtists,
    createArtistSlug,
    dataSource,
    dataReason,
    artistsData,
    pending,
    error,
    refresh
  }
}

